import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowDownLeft, ArrowLeft, Download, Loader2, Pencil, Printer, RefreshCw, Send, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDiscardSalesNote,
  useDownloadSalesNotePdf,
  useEmitSalesNote,
  useRefreshSalesNoteStatus,
  useSalesNote,
} from "@/lib/api/sales-notes.queries";
import { translateNoteError } from "@/components/sales-notes/error-translations";
import { NoteForm } from "@/components/sales-notes/NoteForm";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { useTeamPermission } from "@/lib/team-context";
import { notePdfAction } from "@/components/sales-notes/pdf-actions";
import { downloadBlob } from "@/lib/download-blob";

import { NOTE_STATUS_BADGE_VARIANT } from "@/components/sales-notes/format";
import { NoteArtifactsPanel } from "@/components/sales-notes/NoteArtifactsPanel";
import { NoteCudeCard } from "@/components/sales-notes/NoteCudeCard";
import { NoteStatusTimeline } from "@/components/sales-notes/NoteStatusTimeline";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";
import { useActiveCompany } from "@/lib/company-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";

export const Route = createFileRoute(
  "/companies/$companyId/notas-credito/$noteId/",
)({
  component: CreditNoteDetailPage,
});

function CreditNoteDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CreditNoteDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function CreditNoteDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, noteId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const noteQuery = useSalesNote("credit", companyId, noteId);
  const refresh = useRefreshSalesNoteStatus("credit", companyId);
  const emit = useEmitSalesNote("credit", companyId);
  const discard = useDiscardSalesNote("credit", companyId);
  const canWrite = useTeamPermission("sales_notes.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const downloadPdf = useDownloadSalesNotePdf("credit", companyId);
  const readiness = useCompanyDianReadiness(companyId, "nota_credito");
  const readinessReason = useDianReadinessReason(readiness, t);

  if (noteQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (noteQuery.isError || !noteQuery.data) {
    return (
      <ErrorState
        title={t("notes.detail.error_title")}
        description={t("notes.detail.error_body")}
        onRetry={() => void noteQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const note = noteQuery.data;
  const canRefresh = !!note.processId;
  const canEmit = note.status === "draft" || note.status === "failed";
  // A draft, or a note DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && note.editable;
  const canDiscard = canWrite && note.status === "draft" && note.documentNumber === 0;
  const artifactsEnabled = note.status === "emitted" || note.status === "failed";
  const pdfAction = notePdfAction(note.status);

  async function onRefresh() {
    try {
      await refresh.mutateAsync({ noteId });
      toast.success(t("notes.detail.refresh_done"));
    } catch (err) {
      toast.error(translateNoteError(err, t));
    }
  }

  async function onDiscard() {
    try {
      await discard.mutateAsync({ noteId, expectedRevision: note.revision });
      toast.success(t("notes.detail.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/notas-credito", params: { companyId } });
    } catch (err) {
      toast.error(translateNoteError(err, t));
      setDiscardOpen(false);
    }
  }

  async function onEmit() {
    try {
      await emit.mutateAsync({ noteId });
      toast.success(t("notes.detail.emit_queued"));
    } catch (err) {
      showEmitErrorToast(err, translateNoteError(err, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    }
  }

  async function onDownloadPdf() {
    try {
      const blob = await downloadPdf.mutateAsync({ noteId });
      downloadBlob(blob, `${note.documentId}.pdf`);
    } catch (err) {
      toast.error(translateNoteError(err, t));
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nota_credito" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/notas-credito"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("notes.detail.back")}
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <ArrowDownLeft className="h-5 w-5 text-primary" />
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              {note.documentId}
            </h1>
            <Badge
              variant={NOTE_STATUS_BADGE_VARIANT[note.status]}
              className="text-[10px] uppercase"
            >
              {t(`notes.status.${note.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {note.customerLegalName} - {formatCop(note.payableAmount)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canRefresh ? (
            <Button
              variant="outline"
              onClick={() => void onRefresh()}
              disabled={refresh.isPending}
            >
              <RefreshCw
                className={refresh.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              />
              {t("notes.detail.refresh_status")}
            </Button>
          ) : null}
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("notes.detail.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("notes.detail.discard")}
            </Button>
          ) : null}
          {canEmit && !editing ? (
            <Button
              onClick={() => void onEmit()}
              disabled={emit.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
            >
              <Send className="h-4 w-4" />
              {t("notes.detail.emit_now")}
            </Button>
          ) : null}
          {pdfAction === "download" && !editing ? (
            <Button
              variant="outline"
              onClick={() => void onDownloadPdf()}
              disabled={downloadPdf.isPending}
            >
              {downloadPdf.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {t("notes.detail.download_pdf")}
            </Button>
          ) : null}
          {pdfAction && !editing ? (
            <Button asChild variant="outline">
              <Link
                to="/companies/$companyId/notas-credito/$noteId/preview"
                params={{ companyId, noteId }}
              >
                <Printer className="h-4 w-4" />
                {t(pdfAction === "download" ? "notes.preview.print" : "notes.detail.preview")}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <NoteForm
          noteType="credit"
          companyId={companyId}
          initialNote={note}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      <Tabs defaultValue="overview" className={editing ? "hidden" : "w-full"}>
        <TabsList>
          <TabsTrigger value="overview">{t("notes.detail.tab.overview")}</TabsTrigger>
          <TabsTrigger value="lines">{t("notes.detail.tab.lines")}</TabsTrigger>
          <TabsTrigger value="dian">{t("notes.detail.tab.dian")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("notes.detail.summary.title")}
              </CardTitle>
              <CardDescription>
                {t("notes.detail.summary.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label={t("notes.detail.field.customer")}
                value={note.customerLegalName ?? "-"}
              />
              <Row
                label={t("notes.detail.field.identification")}
                value={note.customerIdentificationNumber ?? "-"}
              />
              <Row
                label={t("notes.detail.field.related_invoice")}
                value={note.relatedDocumentId}
              />
              <Row
                label={t("notes.detail.field.discrepancy_code")}
                value={`${note.discrepancyResponseCode} - ${t(`notes.creditCodes.${note.discrepancyResponseCode}`)}`}
              />
              <Row
                label={t("notes.detail.field.discrepancy_description")}
                value={note.discrepancyDescription}
              />
              <Row
                label={t("notes.detail.field.issue")}
                value={note.issueDateTime}
              />
              <Row
                label={t("notes.detail.field.payment_means")}
                value={t(`paymentMeans.${note.paymentMeansCode}`, { defaultValue: note.paymentMeansCode })}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("notes.totals.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <Row
                label={t("notes.totals.subtotal")}
                value={formatCop(note.lineExtensionAmount)}
              />
              <Row
                label={t("notes.totals.allowance")}
                value={`- ${formatCop(note.allowanceTotalAmount)}`}
              />
              <Row
                label={t("notes.totals.charge")}
                value={`+ ${formatCop(note.chargeTotalAmount)}`}
              />
              <Row
                label={t("notes.totals.tax_exclusive")}
                value={formatCop(note.taxExclusiveAmount)}
                bold
              />
              <Row
                label={t("notes.totals.iva")}
                value={formatCop(note.ivaAmount)}
              />
              {note.icAmount > 0 ? (
                <Row label={t("notes.totals.ic")} value={formatCop(note.icAmount)} />
              ) : null}
              {note.icaAmount > 0 ? (
                <Row label={t("notes.totals.ica")} value={formatCop(note.icaAmount)} />
              ) : null}
              {note.incAmount > 0 ? (
                <Row label={t("notes.totals.inc")} value={formatCop(note.incAmount)} />
              ) : null}
              <Row
                label={t("notes.totals.prepaid")}
                value={`- ${formatCop(note.prepaidAmount)}`}
              />
              <div className="border-t pt-2">
                <Row
                  label={t("notes.totals.payable")}
                  value={formatCop(note.payableAmount)}
                  bold
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lines" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{t("notes.line.description")}</TableHead>
                    <TableHead className="text-right">
                      {t("notes.line.quantity")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("notes.line.unit_price")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("notes.line.subtotal")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("notes.line.tax")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {note.lines.map((line) => (
                    <TableRow key={line.lineId}>
                      <TableCell className="text-xs text-muted-foreground">
                        {line.lineId}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{line.description}</p>
                        {line.sku ? (
                          <p className="font-mono text-[10px] text-muted-foreground">
                            SKU {line.sku}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {line.quantity} {line.unitCode}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.lineExtensionAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(
                          line.ivaAmount +
                            line.icAmount +
                            line.icaAmount +
                            line.incAmount,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dian" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("notes.detail.timeline_title")}
              </CardTitle>
              <CardDescription>
                {t("notes.detail.timeline_subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <NoteStatusTimeline note={note} />
            </CardContent>
          </Card>
          <div className="space-y-4">
            <NoteCudeCard note={note} />
            <NoteArtifactsPanel
              noteType="credit"
              companyId={companyId}
              noteId={noteId}
              enabled={artifactsEnabled}
            />
          </div>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("journal.document.title")}
          </CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="sales_notes"
            documentId={noteId}
          />
        </CardContent>
      </Card>

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("notes.detail.discard_title", { documentId: note.documentId })}
        description={t("notes.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
